import { useEffect, useRef } from 'react';

import { Box, Heading, TextButton } from '@zillow/constellation-10';
import { LoadingHeading } from '@zg-rentals/loading-components';
import { TourStatusForZcheduler } from '@zg-rentals/ts-z-calendar-client';

import DayEventList from './DayEventList';
import TourCountTile from './TourCountTile';
import ToursErrorState from './ToursErrorState';
import { headerStyles, headingStyles, tileRowStyles, widgetStyles } from './styles';
import useListingTourConfig from '../../hooks/useListingTourConfig';
import useUpcomingToursData from '../../hooks/useUpcomingToursData';
import { ToursSummaryTileType } from './types';
import type { ToursSummaryDayChipType } from './types';
import type { UpcomingToursDay } from '../../hooks/useUpcomingToursData';

// Counts render unconditionally, so callers decide whether a listing has touring set up at all.
type ToursSummaryProps = {
  listingAlias: string;
  tourCalendarPath: string;
  inquiriesPath: string;
  showCancelledTours?: boolean;
  // The tour calendar redirects to tour-availability and drops the tourId whenever there's no booked
  // tour and no availability; defaults to false so an omitted prop can't ship links that bounce.
  canDeepLinkToCalendar?: boolean;
  // Analytics stay caller-owned: the widget only reports interactions, the app decides what to emit.
  onImpression?: () => void;
  onViewCalendarClick?: () => void;
  onTileClick?: (tile: ToursSummaryTileType) => void;
  onDayChipClick?: (chip: ToursSummaryDayChipType) => void;
  onTourCardClick?: () => void;
  viewCalendarEventId?: string;
};

type TileConfig = {
  countKey: ToursSummaryTileType;
  label: string;
  status: TourStatusForZcheduler;
  testId: string;
};

const TILES: Array<TileConfig> = [
  {
    countKey: ToursSummaryTileType.Requested,
    label: 'Requests',
    status: TourStatusForZcheduler.REQUESTED,
    testId: 'ToursSummary-requestsTile',
  },
  {
    countKey: ToursSummaryTileType.Booked,
    label: 'Booked',
    status: TourStatusForZcheduler.CONFIRMED,
    testId: 'ToursSummary-bookedTile',
  },
  {
    countKey: ToursSummaryTileType.Canceled,
    label: 'Canceled',
    status: TourStatusForZcheduler.CANCELLED,
    testId: 'ToursSummary-canceledTile',
  },
];

// Preserves any query string or hash already on the path so the deep link can't turn into a dead URL.
const buildTourDeepLink = (tourCalendarPath: string, tourId: string) => {
  const [pathAndQuery = '', hash] = tourCalendarPath.split('#');
  const separator = pathAndQuery.includes('?') ? '&' : '?';
  const deepLink = `${pathAndQuery}${separator}tourId=${encodeURIComponent(tourId)}`;

  return hash ? `${deepLink}#${hash}` : deepLink;
};

// Events are chronological within each day and days are in order, so the first event at or after now is
// the soonest upcoming one. Earlier-today tours still count toward the tile, so they are the fallback
// rather than nothing at all — the calendar opens them fine, they're just less useful to land on.
const getDeepLinkTourIdByStatus = (days: Array<UpcomingToursDay>, nowMs: number) => {
  const upcoming: Partial<Record<TourStatusForZcheduler, string>> = {};
  const earliest: Partial<Record<TourStatusForZcheduler, string>> = {};

  for (const { events } of days) {
    for (const { startMs, tourId, tourStatus } of events) {
      earliest[tourStatus] ??= tourId;
      if (startMs >= nowMs) {
        upcoming[tourStatus] ??= tourId;
      }
    }
  }

  return (status: TourStatusForZcheduler) => upcoming[status] ?? earliest[status];
};

const ToursSummary = ({
  listingAlias,
  tourCalendarPath,
  inquiriesPath,
  showCancelledTours = false,
  canDeepLinkToCalendar = false,
  onImpression,
  onViewCalendarClick,
  onTileClick,
  onDayChipClick,
  onTourCardClick,
  viewCalendarEventId,
}: ToursSummaryProps) => {
  const { counts, days, isError, isPending } = useUpcomingToursData(listingAlias);
  const { isInstantBook, isPending: isTourConfigPending } = useListingTourConfig(listingAlias);

  // An impression means the loaded widget was actually shown, so shimmer/error states and refetches
  // don't fire it. It's per listing (a same-route alias swap doesn't remount) and needs a callback.
  const impressionFiredForAlias = useRef<string>();
  const hasSettled = !isPending && !isTourConfigPending && !isError;
  useEffect(() => {
    if (hasSettled && onImpression && impressionFiredForAlias.current !== listingAlias) {
      impressionFiredForAlias.current = listingAlias;
      onImpression();
    }
  }, [hasSettled, onImpression, listingAlias]);

  // Derived per render rather than memoized: the map is at most one id per status, and reading the clock
  // inside a useMemo would pin "upcoming" to whenever the tours last changed.
  const getDeepLinkTourId = getDeepLinkTourIdByStatus(days, Date.now());

  // A single full-width shimmer stands in for the whole widget; isPending rather than isLoading so a
  // disabled or paused query can't read as settled zeros. The config gates the Requests tile, so wait too.
  if (isPending || isTourConfigPending) {
    return (
      <LoadingHeading isLoading level={2} loadingWidth="100%" data-testid="ToursSummary-loading">
        Tours
      </LoadingHeading>
    );
  }

  // Zero counts are indistinguishable from a failed fetch, so surface the failure instead.
  if (isError) {
    return <ToursErrorState tourCalendarPath={tourCalendarPath} />;
  }

  // Instant-book listings confirm tours without landlord action, so requests never accrue. A failed
  // config fetch falls open to showing the tile — the counts are still real, so hiding them behind
  // the error state would cost more than the extra tile does.
  const visibleTiles = isInstantBook
    ? TILES.filter(({ status }) => status !== TourStatusForZcheduler.REQUESTED)
    : TILES;

  return (
    <Box css={widgetStyles}>
      <Box css={headerStyles}>
        <Heading level={2} css={headingStyles}>
          Tours
        </Heading>

        <TextButton textStyle="body-sm" asChild>
          <a
            href={tourCalendarPath}
            onClick={onViewCalendarClick}
            data-eventid={viewCalendarEventId}
            data-testid="ToursSummary-viewCalendar"
          >
            View calendar
          </a>
        </TextButton>
      </Box>

      <Box css={tileRowStyles}>
        {visibleTiles.map(({ countKey, label, status, testId }) => {
          const count = counts[countKey];
          const deepLinkTourId = getDeepLinkTourId(status);

          // The calendar omits cancelled tours unless the consumer opts in, so linking one would land on
          // its "tour could not be found" toast.
          const canDeepLink =
            canDeepLinkToCalendar && (status !== TourStatusForZcheduler.CANCELLED || showCancelledTours);

          return (
            <TourCountTile
              key={countKey}
              count={count}
              label={label}
              testId={testId}
              href={
                canDeepLink && count > 0 && deepLinkTourId
                  ? buildTourDeepLink(tourCalendarPath, deepLinkTourId)
                  : undefined
              }
              onClick={onTileClick && (() => onTileClick(countKey))}
            />
          );
        })}
      </Box>

      <DayEventList
        days={days}
        inquiriesPath={inquiriesPath}
        onDayChipClick={onDayChipClick}
        onTourCardClick={onTourCardClick}
      />
    </Box>
  );
};

export default ToursSummary;
